// ExcelJS
import ExcelJS from 'exceljs';

// Import html to formatted tags
import htmlToFormattedText from 'html-to-formatted-text';

// Import types
import {
    CalculationLineApi,
    CalculationPercentagesApi,
    CalculationTailApi,
    Column
} from '../types';

// Constants
import {
    CALCULATION_DEFAULT_COLUMNS
} from '../constants';
import { CostsEntity } from '../types/budget-check';
import { Aggregation, ViewModelLineType, ViewModelLine, isCalculationLineApi } from '../state-management/calculation-state';
import { VIRTUAL_SCROLLABLE } from '@angular/cdk/scrolling';

const HEADER_ALIGNMENTS: HorizontalAlignment[] = [
    'center', // Code
    'left', // Description
    'left', // Quantity
    'left', // Unit

    'left', // hour-norm
    'left', // hour-net
    'left', // hour-percent
    'left', // hour-gross

    'left', // material-norm
    'center', // material-net
    'left', // material-percent
    'left', // material-gross

    'left', // equipment-norm
    'center', // equipment-net
    'left', // equipment-percent
    'left', // equipment-gross

    'left', // subcontracting-norm
    'center', // subcontracting-net
    'left', // subcontracting-percent
    'left', // subcontracting-gross

    'left', // norm-total
    'left', // net-total
    'left', // gross-total
];

// Types
type HorizontalAlignment = 'left' | 'center' | 'right' | 'fill' | 'justify' | 'centerContinuous' | 'distributed';
type VerticalAlignment = 'top' | 'middle' | 'bottom' | 'distributed' | 'justify';

type CellDataOptions = Partial<ExcelJS.Style> & Partial<{
    style: Partial<ExcelJS.Style>;
    hAlign: HorizontalAlignment;
    vAlign: VerticalAlignment;
}>;

const BUDGET_COLUMN_NAMES = ['Code', 'Categorie', 'Omschrijving', 'Hoeveelheid', 'Eenheid', 'Uren', 'Inkoop', 'Verkoop'];
const BUDGET_HEADER_ALLIGNMENTS: HorizontalAlignment[] = [
    'center', 'left', 'left', 'right', 'right', 'right', 'right', 'right'
]
const BUDGET_COLUMN_WIDTHS = [10, 10, 50, 12, 10, 12, 20, 20];
const COST_COLUMN_NAMES = ['Project', 'CrediteurNr', 'Crediteur', 'Bedrag'];
const COST_HEADER_ALLIGNMENTS: HorizontalAlignment[] = [
    'right', 'left', 'right', 'right', 'left'
]
const COST_COLUMN_WIDTHS = [15, 15, 35, 35];

declare type CostLine = {
    code: string;
    category: 'a' | 'ma' | 'me' | 'oa' | 'xx';
    description: string;
    quantity: number;
    unit: string;
    hours?: number;
    norm?: number;
    netTotal: number;
    grossTotal: number;
}

export namespace ExcelUtils {
    function createCategroyRow(sheet: ExcelJS.Worksheet, line: ViewModelLine, columns: Column[]) {
        // Add category header row
        const catHeader = sheet.addRow([
            // Code and description
            toCell(line.code), toCell(line.description, { hAlign: 'left' }),
            // Fill empty cells
            ...(new Array(columns.length - 5).fill(toCell(''))),
            // Totals
            toCurrencyCell(line.aggregation?.all.unitPrice), toCurrencyCell(line.aggregation?.all.net), toCurrencyCell(line.aggregation?.all.gross)
        ]);

        // Style cat header
        applyStyle(catHeader, { font: { bold: true, size: 12 }, fill: colorFill('FFD3D3D3') });
    }

    function createSubcategroyRow(sheet: ExcelJS.Worksheet, line: ViewModelLine, columns: Column[]) {
        const subHeader = sheet.addRow([
            // Code and description
            toCell(line.code), toCell(line.description, { hAlign: 'left' }),
            // Fill empty cells
            ...(new Array(columns.length - 2).fill(toCell(''))),
        ]);

        // Style sub header
        applyStyle(subHeader, { font: { bold: true }, fill: colorFill('FFE9E8E8') });
    }

    function createTextRow(sheet: ExcelJS.Worksheet, line: ViewModelLine, columns: Column[]) {
        const calcLine = line.line as CalculationLineApi;

        // TODO: Text formatting
        const textRow = sheet.addRow([
            // General columns
            toCell(''), toCell(htmlToFormattedText(line.line.description), { hAlign: 'left', richText: true }), toIntCell(Number(calcLine.quantity)), toCell(calcLine.unit),
            // Fill empty cells
            ...(new Array(columns.length - 4).fill(toCell(''))),
        ]);

        // Text row styling
        applyStyle(textRow, {
            fill: colorFill('81e4f8e5')
        });
    }

    function createCalculationRow(sheet: ExcelJS.Worksheet, line: ViewModelLine, columns: Column[], percentages: CalculationPercentagesApi) {
        const calcLine = line.line as CalculationLineApi;
        sheet.addRow([
            // General columns
            toCell(''),
            toCell(calcLine?.description ?? '', { hAlign: 'left' }),
            toIntCell(Number(calcLine?.quantity)), toCell(calcLine?.unit),
            // Hours
            toIntCell(line.aggregation.hours.norm), toIntCell((line.aggregation.hours.norm ?? 0) * Number(calcLine?.quantity ?? 0)), toPercentCell(calcLine?.hours?.percent ?? percentages?.hours ?? 0), toCurrencyCell(line.aggregation.hours.gross),
            // Material
            toCurrencyCell(line.aggregation.material.norm), toCurrencyCell(line.aggregation.material.net), toPercentCell(calcLine.material?.percent ?? percentages?.material ?? 0), toCurrencyCell(line.aggregation.material.gross),
            // Equipment
            toCurrencyCell(line.aggregation.equipment.norm), toCurrencyCell(line.aggregation.equipment.net), toPercentCell(calcLine.equipment?.percent ?? percentages?.equipment ?? 0), toCurrencyCell(line.aggregation.equipment.gross),
            // Subcontracting
            toCurrencyCell(line.aggregation.subcontracting.norm),
            toCurrencyCell(line.aggregation.subcontracting.net),
            toPercentCell(calcLine.subcontracting?.percent ?? percentages?.subcontracting ?? 0),
            toCurrencyCell(line.aggregation.subcontracting.gross),
            // Total
            toCurrencyCell(line.aggregation.all.unitPrice),
            toCurrencyCell(line.aggregation.all.net),
            toCurrencyCell(line.aggregation.all.gross)
        ]);
    }

    function createSubtotalRow(sheet: ExcelJS.Worksheet, line: ViewModelLine, columns: Column[], percentages: CalculationPercentagesApi) {
        const tailLine = line.line as CalculationLineApi;
        sheet.addRow([
            // General columns
            toCell(''),
            toCell(line.type === ViewModelLineType.TOTAL ? line.description ?? 'Subtotaal' : '', { hAlign: 'left' }),
            toCell(''), toCell(''),
            // Hours
            toCell(''), toCell(''), toCell(''), toCell(''),
            // Material
            toCell(''), toCell(''), toCell(''), toCell(''),
            // Equipment
            toCell(''), toCell(''), toCell(''), toCell(''),
            // Subcontracting
            toCell(''), toCell(''), toCell(''),
            toCell(''),
            // Total
            toCell(line.type === ViewModelLineType.SUBTOTAL ? line.description ?? 'Subtotaal' : '', { hAlign: 'right' }),
            toCurrencyCell(line.aggregation.all.net, {
                border: {
                    top: {
                        color: {
                            argb: '00000000'
                        }, style: 'thin'
                    }
                }
            }),
            toCurrencyCell(line.aggregation.all.gross, {
                border: {
                    top: {
                        color: {
                            argb: '00000000'
                        }, style: 'thin'
                    }
                }
            })
        ]);
    }

    function createReadOnlyRow(sheet: ExcelJS.Worksheet, line: ViewModelLine, columns: Column[], percentages: CalculationPercentagesApi) {
        const tailLine = line.line as CalculationLineApi;
        sheet.addRow([
            // General columns
            toCell(''),
            toCell(line.description, { hAlign: 'left' }),
            toCell(''), toCell(''),
            // Hours
            toCell(''), toCell(''), toCell(''), toCell(''),
            // Material
            toCell(''), toCell(''), toCell(''), toCell(''),
            // Equipment
            toCell(''), toCell(''), toCell(''), toCell(''),
            // Subcontracting
            toCell(''), toCell(''), toCell(''),
            toCell(''),
            // Total
            toCell(''),
            toCurrencyCell(line.aggregation.all.net),
            toCurrencyCell(line.aggregation.all.gross)
        ]);
    }

    function createCalculationTailRow(sheet: ExcelJS.Worksheet, line: ViewModelLine, columns: Column[], percentages: CalculationPercentagesApi) {
        const tailLine = line.line as CalculationTailApi;
        sheet.addRow([
            // General columns
            toCell(''),
            toCell(line.type === ViewModelLineType.TAIL_SUBTOTAL ? '' : tailLine.description, { hAlign: 'left' }),
            toCell(''), toCell(''),
            // Hours
            toCell(''), toCell(''), toCell(''), toCell(''),
            // Material
            toCell(''), toCell(''), toCell(''), toCell(''),
            // Equipment
            toCell(''), toCell(''), toCell(''), toCell(''),
            // Subcontracting
            toCell(''), toCell(''), toCell(''),
            toCell(''),
            // Total
            toCell(line.type === ViewModelLineType.TAIL_SUBTOTAL ? 'Subtotaal' : '', { hAlign: 'right' }),
            toCell(line.type === ViewModelLineType.TAIL_PERCENTAGE ? `${(tailLine.percent * 100).toFixed(2)}%` : '', { hAlign: 'right' }),
            toCurrencyCell(line.aggregation.all.gross, line.type === ViewModelLineType.TAIL_SUBTOTAL ? {
                border: {
                    top: {
                        color: {
                            argb: '00000000'
                        }, style: 'thin'
                    }
                }
            } : undefined)
        ]);
    }

    export function convertCalculation(lines: ViewModelLine[], totals: Aggregation, defaultPercentages: CalculationPercentagesApi): ExcelJS.Workbook {
        // Create workbook
        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'To Build B.V.';

        // Create sheet in workbook
        const sheet = workbook.addWorksheet('Calculatie');
        (sheet as any)._addRow = sheet.addRow;
        sheet.addRow = expandedAddRow;

        // Get columns
        const columns = Object.keys(CALCULATION_DEFAULT_COLUMNS).map(id => CALCULATION_DEFAULT_COLUMNS[id]).filter(c => !c.hidden) as Column[];

        // Set column widths
        sheet.columns = columns.map(c => ({ width: c.width * 260 }));

        // Add headers to sheet
        sheet.addRow(columns.map((c, i) => ({
            value: c.label ?? c.name,
            font: { bold: true, size: 13 },
            fill: colorFill('FFF2F2F2'),
            alignment: { horizontal: HEADER_ALIGNMENTS[i] }
        }) as Partial<ExcelJS.Cell>));

        for (const line of lines) {
            switch (line.type) {
                case ViewModelLineType.CATEGORY:
                    createCategroyRow(sheet, line, columns);
                    break;
                case ViewModelLineType.SUBCATEGORY:
                    createSubcategroyRow(sheet, line, columns);
                    break;
                case ViewModelLineType.CALCULATION:
                    createCalculationRow(sheet, line, columns, defaultPercentages);
                    break;
                case ViewModelLineType.TEXT:
                    createTextRow(sheet, line, columns);
                    break;
                case ViewModelLineType.SUBTOTAL:
                case ViewModelLineType.TOTAL:
                    createSubtotalRow(sheet, line, columns, defaultPercentages);
                    break;
                case ViewModelLineType.READ_ONLY:
                    createReadOnlyRow(sheet, line, columns, defaultPercentages);
                    break;
                case ViewModelLineType.TAIL_AMOUNT:
                case ViewModelLineType.TAIL_PERCENTAGE:
                case ViewModelLineType.TAIL_PRELIMANARY:
                case ViewModelLineType.TAIL_TEXT:
                case ViewModelLineType.TAIL_SUBTOTAL:
                    createCalculationTailRow(sheet, line, columns, defaultPercentages);
                    break;
            }
        }

        // Return result
        return workbook;
    }

    export function exportCostManagement(lines: ViewModelLine[], invoices: CostsEntity[] = []): ExcelJS.Workbook {
        // Create workbook
        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'To Build B.V.';

        // Create sheet in workbook
        const sheet = workbook.addWorksheet('Begroting');
        (sheet as any)._addRow = sheet.addRow;
        sheet.addRow = expandedAddRow;

        // Flip calculation and keep only lines with costs
        const costLines = lines
            .filter((f) => isCalculationLineApi(f.line))
            .map<CostLine[]>((line) => {
                const calcLine = line.line as CalculationLineApi;

                if (!calcLine || calcLine.isTextLine || !calcLine.quantity) {
                    return [];
                } else {
                    const result: CostLine[] = [];

                    if (!!line.aggregation.hours.norm) {
                        result.push({
                            category: 'a',
                            code: calcLine.code,
                            description: calcLine.description,
                            grossTotal: line.aggregation.hours.gross,
                            netTotal: line.aggregation.hours.net,
                            quantity: Number(calcLine.quantity),
                            unit: calcLine.unit,
                            norm: line.aggregation.hours.norm,
                            hours: line.aggregation.hours.hours
                        });
                    }

                    if (!!line.aggregation.material.norm) {
                        result.push({
                            category: 'ma',
                            code: calcLine.code,
                            description: calcLine.description,
                            grossTotal: line.aggregation.material.gross,
                            netTotal: line.aggregation.material.net,
                            quantity: Number(calcLine.quantity),
                            norm: line.aggregation.material.norm,
                            unit: calcLine.unit,
                        });
                    }

                    if (!!line.aggregation.equipment.norm) {
                        result.push({
                            category: 'me',
                            code: calcLine.code,
                            description: calcLine.description,
                            grossTotal: line.aggregation.equipment.gross,
                            netTotal: line.aggregation.equipment.net,
                            quantity: Number(calcLine.quantity),
                            unit: calcLine.unit,
                            norm: line.aggregation.equipment.norm,
                        });
                    }

                    if (!!line.aggregation.subcontracting.norm) {
                        result.push({
                            category: 'oa',
                            code: calcLine.code,
                            description: calcLine.description,
                            grossTotal: line.aggregation.subcontracting.gross,
                            netTotal: line.aggregation.subcontracting.net,
                            quantity: Number(calcLine.quantity),
                            unit: calcLine.unit,
                            norm: line.aggregation.subcontracting.norm,
                        });
                    }

                    return result;
                }
            }).flat();

        const tailLines = lines.filter(l => [ViewModelLineType.TAIL_AMOUNT, ViewModelLineType.TAIL_PERCENTAGE].includes(l.type))
            .map<CostLine | null>((line) => {
                switch (line.type) {
                    case ViewModelLineType.TAIL_AMOUNT: {
                        const tailLine = line.line as CalculationTailApi;
                        return {
                            category: 'xx',
                            code: 'staart',
                            description: tailLine.description,
                            grossTotal: line.aggregation.all.gross,
                            netTotal: line.aggregation.all.net,
                            quantity: 1,
                            unit: 'staart',
                            norm: tailLine.amount,
                        };
                    }
                    case ViewModelLineType.TAIL_PERCENTAGE: {
                        const tailLine = line.line as CalculationTailApi;
                        return {
                            category: 'xx',
                            code: 'staart',
                            description: tailLine.description,
                            grossTotal: line.aggregation.all.gross,
                            netTotal: line.aggregation.all.net,
                            quantity: 1,
                            unit: 'staart',
                            norm: tailLine.percent,
                        };
                    }
                    default: return null
                }
            })

        // Add grand total
        const grandTotalLine: CostLine = lines.filter(l => l.type === ViewModelLineType.TOTAL)
            .map<CostLine>((line) => ({
                category: 'xx',
                code: 'totaal',
                description: line.description ?? 'Totaal',
                grossTotal: line.aggregation.all.gross,
                netTotal: line.aggregation.all.net,
                quantity: 1,
                unit: 'totaal',
                norm: line.aggregation.all.gross,
            })).pop();

        const allLines = [...costLines, ...tailLines, grandTotalLine].filter((line) => !!line && !!line.grossTotal);

        // Set column widths
        sheet.columns = BUDGET_COLUMN_WIDTHS.map(c => ({ width: c }));

        // Add headers to sheet
        sheet.addRow(BUDGET_COLUMN_NAMES.map((c, i) => ({
            value: c,
            font: { bold: true, size: 13 },
            fill: colorFill('FFF2F2F2'),
            alignment: { horizontal: BUDGET_HEADER_ALLIGNMENTS[i] }
        }) as Partial<ExcelJS.Cell>));

        // Write categories
        for (const line of allLines) {
            // Write line
            sheet.addRow([
                {
                    value: line.code,
                    alignment: 'left'
                },
                {
                    value: line.category,
                    alignment: 'left'
                },
                {
                    value: line.description,
                    alignment: 'left'
                },
                {
                    value: line.quantity,
                    alignment: 'right'
                },
                {
                    value: line.unit,
                    alignment: 'right'
                },
                // {
                //     value: line.norm ?? '',
                //     alignment: 'right'
                // },
                {
                    value: line.hours ?? '',
                    alignment: 'right'
                },
                toCurrencyCell(line.netTotal),
                toCurrencyCell(line.grossTotal),
            ])

            // Add empty row
            // sheet.addRow((new Array(7).fill(toCell(''))));
        }

        if (invoices !== undefined && !!invoices.length) {
            // Create sheet in workbook
            const sheet2 = workbook.addWorksheet('Kosten');
            (sheet2 as any)._addRow = sheet2.addRow;
            sheet2.addRow = expandedAddRow;

            // Set column widths
            sheet2.columns = COST_COLUMN_WIDTHS.map(c => ({ width: c }));

            // Add headers to sheet
            sheet2.addRow(COST_COLUMN_NAMES.map((c, i) => ({
                value: c,
                font: { bold: true, size: 13 },
                fill: colorFill('FFF2F2F2'),
                alignment: { horizontal: COST_HEADER_ALLIGNMENTS[i] }
            }) as Partial<ExcelJS.Cell>));

            // Due to an update to the data collection, aggregation is now needed to combine all costs for a single creditor
            const aggregatedCosts = (invoices as CostsEntity[]).reduce((acc, cost) => {
                const creditorKey = cost.creditorId || 'To Build algemeen';
                if (!acc[creditorKey]) {
                    // On first occurence of a creditor, add it to the accumulator
                    acc[creditorKey] = { ...cost };
                } else {
                    // After that, add up the costs for that creditor
                    // So the other fields will just be from the first occurence
                    acc[creditorKey].totalCosts += cost.totalCosts;
                }
                return acc;
            }, {} as Record<string, CostsEntity>);

            // Calculate total
            let totalCosts = 0;

            for (const invoice of Object.values(aggregatedCosts)) {
                sheet2.addRow([
                    {
                        value: invoice.project,
                        alignment: 'left'
                    },
                    {
                        value: invoice.creditorId || '-----',
                        alignment: 'left',
                    },
                    {
                        value: invoice.creditorName || 'To Build algemeen',
                        alignment: 'left'
                    },
                    toCurrencyCell(invoice.totalCosts)
                ])

                // Add to total
                totalCosts += invoice.totalCosts;
            }

            sheet2.addRow([
                toCell(''),
                toCell(''),
                toCell('Totaal', { hAlign: 'right', font: { bold: true } }),
                toCurrencyCell(totalCosts, {
                    border: {
                        top: {
                            color: {
                                argb: '00000000'
                            }, style: 'thin'
                        }
                    },
                    font: { bold: true }
                })
            ])

            // Add third sheet with pivot table data
            // Just dump all properties of all items in the data collection
            // Also add headers with the property names
            const sheet3 = workbook.addWorksheet('Kosten Pivot');
            (sheet3 as any)._addRow = sheet3.addRow;
            sheet3.addRow = expandedAddRow;

            // Assume that all items have the same properties, so just take the first one to get the headers
            const pivotHeaders = Object.keys(invoices[0] ?? {});

            // Add headers to sheet
            sheet3.addRow(pivotHeaders.map((c) => ({
                value: c,
                font: { bold: true, size: 13 },
                fill: colorFill('FFF2F2F2'),
                alignment: { horizontal: 'left' }
            }) as Partial<ExcelJS.Cell>));

            for (const invoice of invoices) {
                sheet3.addRow(pivotHeaders.map((header) => {
                    const value = (invoice as any)[header];
                    if (typeof value === 'number') {
                        return toCell(value);
                    } else {
                        return toCell(value, { hAlign: 'left' });
                    }
                }))
            }
        }

        return workbook;
    }
}

/* --- Helpers --- */

function expandedAddRow(data: any, style?: string): ExcelJS.Row {
    // Check if data is array of cells
    if (Array.isArray(data) && data.every(v => typeof (v) === 'object')) {
        // Add values
        const row = this._addRow(data.map(cell => cell.value), style);

        // Set cell properties
        (data as ExcelJS.Cell[]).forEach((cellData, i) => {
            // Get the cell
            const cell = row.getCell(i + 1);

            // Set cell properties
            Object.assign(cell, cellData);
        });

        // Return result
        return row;
    } else {
        return this._addRow(data, style);
    }
}

function applyStyle(target: ExcelJS.Row, style: Partial<ExcelJS.Style>, maxColumn?: number): void {
    new Array(Math.min(maxColumn ? maxColumn + 1 : target.cellCount)).fill(null).forEach((_, i) => {
        // Get cell
        const cell = target.getCell(i + 1);

        // Update style
        Object.assign(cell.style, style);
    });
}

function toCell(value?: string | number, options: { richText?: boolean } & CellDataOptions = {}): Partial<ExcelJS.Cell> {
    // Convert alignment
    options.alignment = convertAlignment({ hAlign: !options.numFmt ? 'center' : undefined, ...options });

    if (options.richText) {
        return { value, ...options } as any;
    } else {
        return { value, ...options }
    }
}

function toDecimalCell(value?: number, options: Partial<Omit<CellDataOptions, 'numFmt'>> = {}): Partial<ExcelJS.Cell> {
    return toCell(value, { ...options, numFmt: '0.00' });
}

function toIntCell(value?: number, options: Partial<Omit<CellDataOptions, 'numFmt'>> = {}): Partial<ExcelJS.Cell> {
    return toCell(value, { hAlign: 'center', ...options, numFmt: '0', border: !!value ? options.border : undefined });
}

function toAdditionCell(value?: number, options: Partial<Omit<CellDataOptions, 'numFmt'>> = {}): Partial<ExcelJS.Cell> {
    return toCell(value, { ...options, numFmt: '+ 0.00', border: !!value ? options.border : undefined });
}

function toSubtractCell(value?: number, options: Partial<Omit<CellDataOptions, 'numFmt'>> = {}): Partial<ExcelJS.Cell> {
    return toCell(value, { ...options, numFmt: '- 0.00', border: !!value ? options.border : undefined });
}

function toCurrencyCell(value?: number, options: Partial<Omit<CellDataOptions, 'numFmt'>> = {}): Partial<ExcelJS.Cell> {
    return toCell(value, { ...options, numFmt: '€ #,##0.00', border: !!value ? options.border : undefined });
}

function toPercentCell(value?: number, options: Partial<Omit<CellDataOptions, 'numFmt'>> = {}): Partial<ExcelJS.Cell> {
    return toCell(value, { hAlign: 'center', ...options, numFmt: '0%' });
}

function colorFill(color: string): ExcelJS.Fill {
    return { type: 'pattern', pattern: 'solid', fgColor: { 'argb': color } };
}

function convertAlignment(options: CellDataOptions): Partial<ExcelJS.Alignment> {
    return {
        ...options.alignment,
        ...(options.hAlign ? { horizontal: options.hAlign } : {}),
        ...(options.vAlign ? { vertical: options.vAlign } : {}),
    }
}